import { useCallback, useEffect, useState } from 'react';
import {
  GetAssetPipelineFlowCommand,
  getIvtClient,
  type GetAssetPipelineFlowOutput,
} from '../../client/IvtHubClient';
import { type DenialKind, denialKindOf } from '../../utils/authorization';
import type { WindowRange } from './components/windowPresets';

export interface AssetFlowState {
  data: GetAssetPipelineFlowOutput | null;
  loading: boolean;
  error: string | null;
  /** Same split as {@link useIndustryFlow}: a 403 must not offer a retry button. */
  denial: DenialKind | null;
  reload: () => void;
}

/**
 * One call per window for the whole STO & IS/ISA view (DAT-9005).
 *
 * Deliberately a sibling of `useIndustryFlow` rather than a generalisation of it: the
 * two operations differ in authorization tier (this one is ANY_MEMBER, the GM one is
 * ADMIN_ONLY) and in payload, and folding them into one hook would put a branch on the
 * one path where both views' load states have to stay distinguishable.
 *
 * `industry` is part of the request scope, so it sits beside the window and a change to
 * either refetches. The STO view queries exactly ONE industry (a product decision), so every
 * request carries `industries: [industry]`, and the contract's "omit the member = every
 * industry I may see" form is deliberately unreachable from here.
 *
 * `null` is NOT "all" any more: it is "no industry resolved yet" — the page is still loading
 * the permitted set, or it failed, or it is empty — and nothing is sent. `loading` stays
 * raised while enabled, because the view genuinely has no answer for its scope yet; the page
 * owns telling the failed and empty cases apart from a real load.
 *
 * A single industry rather than a list because the selector is single-select; the contract
 * member is a list, so widening this is additive.
 *
 * Unlike the GM view, an empty result is NOT read as "no permission". This view is
 * counted from the assets table outward, so a permitted caller with no UC-linked asset
 * in scope legitimately gets zero rows, and the totals are still meaningful.
 */
export function useAssetFlow(
  range: WindowRange,
  industry: string | null,
  enabled = true,
): AssetFlowState {
  const [data, setData] = useState<GetAssetPipelineFlowOutput | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [denial, setDenial] = useState<DenialKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { startDate, endDate } = range;

  useEffect(() => {
    let cancelled = false;
    // The view the user is not looking at must not fetch — see DashboardPage for why both
    // hooks are mounted. `loading` is cleared rather than left as it was: switching away
    // mid-flight cancels the in-flight settle, which would otherwise strand it at true.
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // No industry, no request — see above. Returning here is what keeps an "all industries"
    // request off the wire while the permitted set loads.
    if (industry === null) return;
    // `denial` is cleared on SUCCESS, not here — see useIndustryFlow for the bug that
    // clearing it at request start caused (refused figures reachable for the duration of
    // the next request).
    (async () => {
      try {
        const out = await getIvtClient().send(
          new GetAssetPipelineFlowCommand({
            startDate,
            endDate,
            // Always exactly one — `industry` is non-null past the guard above.
            industries: [industry],
          }),
        );
        if (!cancelled) {
          setData(out);
          setDenial(null);
        }
      } catch (e) {
        // Classified BEFORE the message is flattened: `denialKindOf` reads `reason`,
        // `name` and `$metadata.httpStatusCode` off the thrown object, none of which
        // survive `(e as Error).message`.
        if (!cancelled) {
          const kind = denialKindOf(e);
          setDenial(kind);
          // Keep the previous window's numbers through a transient failure, but DROP them
          // on a denial: it is the cache that makes refused figures reachable at all.
          if (kind !== null) setData(null);
          setError((e as Error).message ?? 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, industry, reloadKey, enabled]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, denial, reload };
}
